// The script of the Studio's pages that show games: the landing page, whose
// shelf has a card per game, the index /games/, which is that shelf alone,
// and a game's own page. It gives each game's
// Download and Remix their dialogs (./gate.ts), takes over the download
// panel a game's page was written with, and goes on with what an address
// asks for: a package wanted from the play page, a sign-in or a payment that
// left the page and came back.
//
//
// It is also what the two pages say to the event log (docs/events.md): the
// page shown, with where the visit came from, and each link followed. A link
// to a game's own address takes the visit along.
//
// The pages read whole without it: every action is a link that works.

import type { GameFacts } from "../core/protocol.ts";
import { enableEvents, track } from "../api/events.ts";
import { carry, currentVisit } from "../api/visit.ts";
import { lang } from "../ui/lang.ts";
import { Gate, readFacts } from "./gate.ts";
import { startPixels } from "./pixels.ts";
import { fillShelf } from "./shelf.ts";

const page = document.querySelector<HTMLElement>("main.game");
// An index says what the landing page says of a link, `at` the index (its cards say so too: caseHtml), and is no view of the landing page.
const index = document.querySelector("main.index") !== null;
const gate = new Gate(page ? { event: "listing.followed" } : { event: "landing.followed", at: index ? "index" : "shelf" });
// Nothing is said from a file. (Nor from a browser that asks not to be tracked: src/api/events.ts.)
if (/^https?:$/.test(location.protocol)) enableEvents();

// The page shown, once: where its visit came from, and whether the visit began here.
const visit = currentVisit();
if (page) {
  const here = new URL(location.href);
  const from = here.searchParams.get("from");
  track("listing.viewed", { app: page.dataset.app, props: { from: from === "landing" || from === "player" ? from : from === "games" ? "index" : "other", first: visit.first } });
  if (here.searchParams.has("from")) {
    here.searchParams.delete("from");
    history.replaceState(history.state, "", here);
  }
} else if (!index) {
  track("landing.viewed", { props: { first: visit.first } });
}

// Each link followed: on a game's page the links of the page itself, on the landing page every link that says where it leads.
document.addEventListener("click", (event) => {
  const link = event.target instanceof Element ? event.target.closest<HTMLAnchorElement>("a[data-to]") : null;
  const to = link?.dataset.to;
  if (!link || !to) return;
  if (!page) track("landing.followed", { app: link.dataset.app, props: { to, ...(link.dataset.at ? { at: link.dataset.at } : {}) } });
  else if (page.contains(link)) track("listing.followed", { app: page.dataset.app, props: { to } });
  takeAlong(link);
});
// (a link opened in another tab with the middle button takes the visit along too)
document.addEventListener("auxclick", (event) => {
  const link = event.target instanceof Element ? event.target.closest<HTMLAnchorElement>("a[data-to]") : null;
  if (link) takeAlong(link);
});

// A form that leads to the room says so as a link does: what was typed is not said.
document.addEventListener("submit", (event) => {
  const form = event.target instanceof HTMLFormElement && event.target.dataset.to ? event.target : null;
  if (form) track("landing.followed", { props: { to: form.dataset.to!, ...(form.dataset.at ? { at: form.dataset.at } : {}) } });
});

/** A link to a game's own address carries the visit there: that address is another origin, with a store of its own. */
function takeAlong(link: HTMLAnchorElement): void {
  if (link.dataset.to === "play" || link.dataset.to === "browser") link.href = carry(link.href);
}

// The landing page: what moves behind it, and its shelf of games.
if (!page) startPixels();
fillShelf();

/** The facts a game's page was written from. */
let own: GameFacts | null = null;
if (page) {
  try {
    own = JSON.parse(document.getElementById("game-facts")?.textContent ?? "null") as GameFacts | null;
  } catch {
    own = null;
  }
  if (own) {
    gate.bind(page, own);
    gate.resume(own);
    // The bar says who is signed in. (The landing page asks only once a visitor reaches for something.)
    void gate.who().catch(() => null);
  }
}

/** Facts read for the landing page's cards, by label: one request a game. */
const read = new Map<string, Promise<GameFacts>>();
const factsOf = (slug: string): Promise<GameFacts> => {
  if (own && own.slug === slug) return Promise.resolve(own);
  let facts = read.get(slug);
  if (!facts) {
    facts = readFacts(slug, lang());
    facts.catch(() => read.delete(slug));
    read.set(slug, facts);
  }
  return facts;
};

// The landing page, back from a sign-in that left it: its address names the game the wish is for.
const wished = page ? null : new URL(location.href).searchParams.get("game");
if (wished && /^[a-z0-9-]{1,32}$/.test(wished)) void factsOf(wished).then((facts) => gate.resume(facts), () => null);

// A game's Download and Remix, wherever a card or a page draws them (src/core/offer.ts, ./shelf.ts).
document.addEventListener("click", (event) => {
  const link = event.target instanceof Element ? event.target.closest<HTMLAnchorElement>("a[data-act]") : null;
  if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.defaultPrevented) return;
  const act = link.dataset.act;
  if (act !== "download" && act !== "remix") return;
  // On a game's page the download panel is in the page: the link scrolls to it.
  if (page && act === "download") return;
  const slug = link.closest<HTMLElement>("[data-slug]")?.dataset.slug;
  if (!slug) return;
  event.preventDefault();
  void factsOf(slug).then(
    (facts) => (act === "download" ? gate.download(facts) : void gate.remix(facts)),
    // The facts could not be read: the link's own address is the way.
    () => location.assign(link.href),
  );
});
